import { useCallback } from 'react';

import { formatDateField, Queries } from '@aline/common';
import { isValid, parseISO } from 'date-fns';
import { usePDFFieldAPI } from 'src/components/viewer/hooks/crud';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import { useMaybeEditorRuntime } from 'src/editor/editor-runtime-registry';
import { Apollo } from 'src/gql/generated';
import { useCurrentUserId } from 'src/gql/hooks/user';
import { useGetIsPDFFieldInDocument } from 'src/helpers/input-fields';
import { useAuthorizedTransforms } from 'src/hooks/authorized-transforms';
import { useGetCurrentDoc } from 'src/hooks/document';
import { useMaybeEditor } from 'src/layout-engine/hooks/editor-hooks';
import { useEditingMode } from 'src/store/document/selectors';

import { getLiveNonSigFieldText } from './get-live-non-sig-field-text';
import { useIsFieldInDocJSON } from './helpers';
import type { PanelVariableType } from './VariableRow';
import { clearUndoHistory, withClearedUndoHistory } from './with-cleared-undo-history';

const FILLED_FOR_ANOTHER_SIGNER_REASON =
  'The date is filled and assigned to another signer, so its format cannot change';

/**
 * Switches a Date field's format: rewrites the placed text first, then saves
 * the format, and writes the old text back if the save fails, so the text and
 * the stored format stay in step. The old text is read from the document, not
 * derived from the stored format, which lags while an earlier switch is still
 * saving. A field that is not placed has no text to rewrite, so only the format
 * is saved.
 *
 * Every switch clears the undo history, also when the text reads the same in
 * both formats, the date was cleared, or the field is not placed: undo would
 * bring back an earlier date in the old format's shape, read in the new one, or
 * leave the label next to the date.
 */
export function useSwitchDateFieldFormat(
  variable: Pick<
    PanelVariableType,
    '__typename' | 'action' | 'dateFormat' | 'id' | 'nodeId' | 'value'
  >
): {
  /** Why the format cannot change now, or '' when it can. */
  blockedReason: string;
  switchDateFormat: (dateFormat: Apollo.DateFieldFormat) => Promise<void>;
} {
  const [updateVariable] = Apollo.useUpdateDocumentVariableMutation();
  const editor = useMaybeEditor();
  const { document } = useGetCurrentDoc();
  const { editingMode } = useEditingMode();
  const currentUserId = useCurrentUserId();
  const documentViewer = useMaybeDocumentViewer();
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const { getPDFFieldValue, setPDFFieldValue } = usePDFFieldAPI(documentViewer, tabId);
  const { getIsPDFFieldInDoc } = useGetIsPDFFieldInDocument(tabId);
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { isInChildren } = useIsFieldInDocJSON(variable);
  // Templates skip the assignee check so anyone can fill a field to test it.
  const fieldAssigneeId =
    document?.variant === Apollo.DocumentVariant.Document
      ? (variable.action?.assignee?.id ?? null)
      : null;
  const canFillField =
    document?.latestVersion.__typename !== 'DocumentVersion' ||
    !editor ||
    Queries.isUserAuthorizedToCompleteNonSigField(editor, { assigneeId: fieldAssigneeId })
      .isUserAuthorized;

  const getPlacedText = useCallback((): string | null => {
    switch (document?.latestVersion.__typename) {
      case 'DocumentVersion':
        return editor ? getLiveNonSigFieldText(editor, variable.nodeId) : null;
      case 'StaticFileVersion':
        return getIsPDFFieldInDoc(variable.nodeId) ? getPDFFieldValue(variable.nodeId) : null;
      default:
        return null;
    }
  }, [document, editor, getIsPDFFieldInDoc, getPDFFieldValue, variable.nodeId]);

  /** Writes `text` into every placement; reports whether the document text changed. */
  const rewriteText = useCallback(
    (text: string): boolean => {
      switch (document?.latestVersion.__typename) {
        case 'DocumentVersion': {
          if (!editor || !currentUserId || !editingMode) return false;
          let updated = false;
          withClearedUndoHistory(editor, () => {
            ({ updated } = AuthorizedTransforms.updateInputField(editor, {
              assigneeId: fieldAssigneeId,
              authorId: currentUserId,
              editingMode,
              fieldId: variable.nodeId,
              update: { text },
            }));
          });
          return updated;
        }
        case 'StaticFileVersion': {
          if (getPDFFieldValue(variable.nodeId) === text) return false;
          setPDFFieldValue(variable.nodeId, text);
          return true;
        }
        default:
          return false;
      }
    },
    [
      AuthorizedTransforms,
      currentUserId,
      document,
      editingMode,
      editor,
      fieldAssigneeId,
      getPDFFieldValue,
      setPDFFieldValue,
      variable.nodeId,
    ]
  );

  const switchDateFormat = useCallback(
    async (dateFormat: Apollo.DateFieldFormat): Promise<void> => {
      const date = variable.value ? parseISO(variable.value) : null;
      const nextText = date && isValid(date) ? formatDateField(date, dateFormat) : null;
      const placedText = nextText === null ? null : getPlacedText();
      const isTextChanging = placedText !== null && placedText !== nextText;
      if (placedText !== null && nextText !== null) {
        if (!canFillField) return;
        // Runs even when the text already matches, so the undo history is cleared.
        const updated = rewriteText(nextText);
        if (isTextChanging && !updated) return;
      } else if (editor) {
        clearUndoHistory(editor);
      }

      const { error } = await updateVariable({
        variables: { input: { id: variable.id, props: { dateFormat } } },
      });
      if (error && isTextChanging) rewriteText(placedText);
    },
    [canFillField, editor, getPlacedText, rewriteText, updateVariable, variable.id, variable.value]
  );

  const blockedReason =
    variable.value && isInChildren && !canFillField ? FILLED_FOR_ANOTHER_SIGNER_REASON : '';

  return { blockedReason, switchDateFormat };
}
